import { Link, navigate, useLocation } from "../navigation";
import { lazy, Suspense, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import type { LearnerExam, LearnerQuestion } from "../../../../packages/content-schema";
import type { LibraryExam } from "../../../../packages/published-content/library";
import { fixedItems } from "../../../../packages/question-presentation/fixed-practice";
import { previewModules, routeLabel } from "../../../../packages/question-presentation/exam-preview";
import { ExamRouteControls } from "../../../../packages/test-ui/exam-route-controls";
import { supportsTiming } from "../../../../packages/question-presentation/timing";
import { AssignDialog } from "../AssignDialog";
import { StartDialog } from "./StartDialog";
import { PageHeading, RequestState } from "../PortalShell";
import { useResource, displayTitle } from "../portal-data";

const TestQuestion = lazy(() => import("../TestQuestion").then((module) => ({ default: module.TestQuestion })));

export function AdminTestPreview({ examId }: { examId: string }) {
  const resource = useResource<{ exam: LearnerExam; releaseId: string }>(`/api/admin/exams/${encodeURIComponent(examId)}`);
  const location = useLocation();
  const [assign, setAssign] = useState(false);
  const [practice, setPractice] = useState(false);
  const exam = resource.data?.exam;
  const routes = Object.fromEntries(["reading_writing", "math"].map(section => [section, location.searchParams.get(`${section}Route`) ?? ""]));
  const requestedModule = exam?.modules.find(m => m.id === location.searchParams.get("module"));
  if (requestedModule?.stage === 2) routes[requestedModule.section] = requestedModule.id;
  const modules = exam ? previewModules(exam.modules, routes) : [];
  const items = exam ? fixedItems({ ...exam, modules }) : [];
  const found = items.findIndex(it => it.moduleId === requestedModule?.id && it.position === Number(location.searchParams.get("position")));
  const index = Math.max(0, found);
  function select(index: number, section?: string, moduleId?: string) {
    const target = new URL(location);
    for (const module of modules.filter(m => m.stage === 2)) target.searchParams.set(`${module.section}Route`, module.id);
    if (section && moduleId) target.searchParams.set(`${section}Route`, moduleId);
    const next = section && moduleId ? { moduleId, position: 1 } : items[index];
    if (!next) return;
    target.searchParams.set("module", next.moduleId);
    target.searchParams.set("position", String(next.position));
    navigate(target.pathname + target.search, true);
  }
  const item = items[index];
  const module = exam?.modules.find((m) => m.id === item?.moduleId);
  const summary: LibraryExam | null = exam ? {
    id: exam.id,
    title: exam.title,
    sitting: exam.sitting,
    eligibility: exam.eligibility,
    available: items.length,
    expected: modules.reduce((total, m) => total + m.expectedCount, 0),
    supportsTiming: supportsTiming(exam)
  } : null;

  return (
    <>
      <PageHeading eyebrow="Test preview" title={exam ? displayTitle(exam.title) : "Test"}>
        <Link className="secondary-button" href="/admin/tests">
          <ChevronLeft size={15} />Tests
        </Link>
        {summary && <button className="secondary-button" onClick={() => setPractice(true)}>Try test</button>}
        {exam && (
          <button className="primary-button" onClick={() => setAssign(true)}>
            <Plus size={15} />Assign test
          </button>
        )}
      </PageHeading>
      <div className="portal-content">
        <RequestState loading={resource.loading} error={resource.error} retry={resource.reload} />
        {exam && item && (
          <>
            <ExamRouteControls modules={exam.modules} routes={routes} onChange={(section, moduleId) => select(0, section, moduleId)} />
            <div className="preview-toolbar">
              <div>
                <strong>{module?.section === "math" ? "Math" : "Reading and Writing"}</strong>
                <span>Module {module?.stage}{module?.stage === 2 ? ` · ${routeLabel(module.routeBand)} route` : ""} · Q{item.position}</span>
              </div>
              <label>
                Question
                <select aria-label="Preview question" value={index} onChange={(event) => select(Number(event.target.value))}>
                  {items.map((it, idx) => (
                    <option key={`${it.moduleId}:${it.position}`} value={idx}>
                      {idx + 1} of {items.length} · {it.moduleId === item.moduleId ? `Q${it.position}` : `${modules.find(m => m.id === it.moduleId)?.section === "math" ? "Math" : "RW"} M${modules.find(m => m.id === it.moduleId)?.stage} Q${it.position}`}
                    </option>
                  ))}
                </select>
              </label>
              <div className="preview-arrows">
                <button className="secondary-button" aria-label="Previous question" disabled={index === 0} onClick={() => select(index - 1)}>
                  <ChevronLeft size={16} />
                </button>
                <button className="secondary-button" aria-label="Next question" disabled={index === items.length - 1} onClick={() => select(index + 1)}>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="admin-preview">
              <PreviewQuestion key={item.questionId} questionId={item.questionId} releaseId={resource.data!.releaseId} />
            </div>
          </>
        )}
      </div>
      {assign && summary && (
        <AssignDialog
          exams={[summary]}
          initialExamId={examId}
          onClose={() => setAssign(false)}
          onAssigned={() => { navigate("/admin/assignments"); }}
        />
      )}
      {practice && summary && <StartDialog exam={summary} onClose={() => setPractice(false)} />}
    </>
  );
}

function PreviewQuestion({ questionId, releaseId }: { questionId: string; releaseId: string }) {
  const resource = useResource<{ question: LearnerQuestion }>(`/api/questions/${encodeURIComponent(questionId)}?release=${encodeURIComponent(releaseId)}`);
  return (
    <>
      <RequestState loading={resource.loading} error={resource.error} retry={resource.reload} />
      {resource.data && (
        <Suspense fallback={<p role="status" className="portal-loading">Loading question...</p>} key={resource.data.question.id}>
          <TestQuestion
            question={resource.data.question}
            releaseId={releaseId}
            selected={null}
            eliminated={new Set()}
            disabled
            onSelect={() => {}}
            onToggleElimination={() => {}}
          />
        </Suspense>
      )}
    </>
  );
}
